import {Injectable} from '@angular/core';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {Observable} from 'rxjs';
import {
  Collector,
  CollectorType, Device,
  Equipment,
  Level,
  Metadata,
  MetadataType,
  VirtualFile
} from '@app/graphql-typings/graphql';
import {CollectorSearchTerm, CollectorsService} from '@shared/services/api/collectors.service';
import {LevelZoneService} from '@app/layout/sites/manage/manage-sites/upsert-site/forms/level-zone-form/level-zone.service';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {map} from 'rxjs/operators';
import {EquipmentScopeOptions, EquipmentService} from '@shared/services/api/equipment.service';
import {MetadataService} from '@shared/services/api/metadata.service';
import {MetadataTypeService} from '@shared/services/api/metadata-type.service';
import {VacerFilesService} from '@app/layout/sitemanager/vacers/vacer-files/vacer-files.service';
import {BacerDevicesService} from '@app/layout/sitemanager/bacers/bacer-devices/bacer-devices.service';


@Injectable({
  providedIn: 'root'
})
export class NavigationCacheService {
  private readonly logger: AceLogger;

  constructor(private collectorsService: CollectorsService,
              private levelZoneService: LevelZoneService,
              private equipmentService: EquipmentService,
              private metadataService: MetadataService,
              private metadataTypeService: MetadataTypeService,
              private filesService: VacerFilesService,
              private deviceService: BacerDevicesService,
              logService: AceLoggerService) {
    this.logger = logService.create(NavigationCacheService.name);
  }


  public getSiteCollector(siteId: number, collectorType: CollectorType): Observable<Collector>{
    return this.getCollectors({site_id: siteId, collector_type: collectorType}).pipe(map((collectors: Collector[]) => this.verifyCollector(collectors, collectorType)));
  }

  public getCollectors(searchTerm: CollectorSearchTerm): Observable<Collector[]>{
    return this.collectorsService.getAllCollectors(searchTerm, {fetchLatestNote: true});
  }

  private verifyCollector(collectors: Collector[], collectorType: CollectorType): Collector {
    if (collectors.length > 1) {
      // ACED allows multiple physical collectors per site but this is not supported by the front end yet.
      // To date, there are no sites with multiple physical collectors in ACED
      throw new Error(`Multiple ${collectorType} collector types found on site`);
    } else {
      return CollectionUtils.only(collectors);
    }
  }

  public getSiteLevels(siteId: number): Observable<Level[]> {
    return this.levelZoneService.getLevelsBySiteId(siteId);
  }

  public getEquipment(collectorId: number, collectorType: CollectorType): Observable<Equipment[]> {
    return this.equipmentService.searchAllEquipment({collector_id: collectorId, commissionable: true}, this.getEquipmentScopeOptions(collectorType));
  }

  public getEquipmentScopeOptions(collectorType: CollectorType): EquipmentScopeOptions {
    return {
      getDeviceData: collectorType === CollectorType.Physical,
      getFilesData: collectorType === CollectorType.Virtual
    };
  }

  public getEquipmentForSite(siteId): Observable<Equipment[]>{
    return this.equipmentService.searchAllEquipment({site_id: siteId, commissionable: true}, {getDeviceData: true, getFilesData: true});
  }

  public getMetadata(): Observable<Metadata[]> {
    return this.metadataService.getAllMetadata({commissionable: true});
  }

  public getMetadataTypes(): Observable<MetadataType[]> {
    return this.metadataTypeService.getAllMetadataTypes({commissionable: true});
  }

  public getFiles(siteId): Observable<VirtualFile[]>{
    return this.filesService.searchAllFiles({site_id: siteId}, {getFileFormat: true});
  }

  public getDevices(collectorId): Observable<Device[]> {
    return this.deviceService.getAllDevices({collector_id: collectorId});
  }
}
